/* =============================================================================
   Activity recorder — feeds Account Setup -> Event Logs & History ->
   "Browse User Insights". Records page views, clicks, committed searches and
   notable actions for every signed-in user.

   DELIBERATELY NOT USING httpClient:
     * it imports router AND store, and the router imports THIS module — a cycle
       that can hand back an undefined export at module-init time;
     * it fires NProgress on every request, so the loading bar would flash on
       every click.
   Plain fetch has neither problem and supports keepalive for the unload flush
   (navigator.sendBeacon cannot set an Authorization header, so it is unusable
   against an authenticated endpoint).

   NOTHING HERE IS LOAD-BEARING. Every entry point is wrapped so a recorder fault
   can never break a navigation, a click, or a search. Failures are silent by
   design: this is telemetry, not a feature the user is waiting on.

   The SERVER decides who did it (JWT) and re-derives the safe route path. What we
   send is only ever WHAT happened. See ActivityController for the redaction
   contract; the client-side stripping below is a second line, not the only one.
   ============================================================================= */

import config from "@/assets/js/appsettings.json";

const API = process.env.VUE_APP_API_URL || config.API_URL;
const ENDPOINT = String(API || "").replace(/\/+$/, "") + "/activity";

const FLUSH_MS = 10000;   // batch window — a click storm costs one request per 10s
const MAX_QUEUE = 200;    // matches the server's per-request cap
const DUP_MS = 500;       // identical consecutive events inside this window collapse

let queue = [];
let timer = null;
let lastKey = "";
let lastAt = 0;
let dropped = 0;

/* Per-TAB id, so two tabs read as two visits (which is what they are). */
function sessionId() {
  try {
    let s = sessionStorage.getItem("jcs_activity_sid");
    if (!s) {
      s = (window.crypto && window.crypto.randomUUID)
        ? window.crypto.randomUUID()
        : "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
            const r = (Math.random() * 16) | 0;
            return (c === "x" ? r : (r & 0x3) | 0x8).toString(16);
          });
      sessionStorage.setItem("jcs_activity_sid", s);
    }
    return s;
  } catch (e) { return null; }
}

/* Whose account this session is acting on, when an admin is previewing someone.
   Read from localStorage rather than the store to avoid an import cycle; the
   server ignores it unless the CALLER is a Master, so it cannot be abused. */
function viewAsUserId() {
  try {
    const raw = localStorage.getItem("viewAsUser");
    if (!raw) return null;
    const u = JSON.parse(raw);
    return (u && u.id) ? String(u.id) : null;
  } catch (e) { return null; }
}

function signedIn() {
  try { return !!localStorage.token; } catch (e) { return false; }
}

function companyCode() {
  try { return localStorage.companyCode || null; } catch (e) { return null; }
}

function clip(s, n) {
  if (s === null || s === undefined) return null;
  const t = String(s).trim();
  if (!t) return null;
  return t.length <= n ? t : t.slice(0, n);
}

function push(ev) {
  if (!signedIn() || !ev || !ev.kind) return;

  // Collapse an identical event repeated within DUP_MS. Note this compares the
  // WHOLE event, not just the kind — an earlier design compared only user+kind+
  // time, which silently merged three different pages visited in five seconds
  // and deleted exactly the fast navigation a support trail is for.
  const key = JSON.stringify(ev);
  const now = Date.now();
  if (key === lastKey && (now - lastAt) < DUP_MS) return;
  lastKey = key;
  lastAt = now;

  if (queue.length >= MAX_QUEUE) { dropped++; queue.shift(); }
  queue.push(ev);

  if (!timer) timer = setTimeout(() => flush(false), FLUSH_MS);
}

function flush(unloading) {
  if (timer) { clearTimeout(timer); timer = null; }
  if (!queue.length || !signedIn()) return;

  // The unload path uses keepalive, whose TOTAL body budget is 64 KiB per the Fetch
  // standard — over that, fetch() rejects outright rather than sending a truncated body,
  // and the rejection lands in the no-op catch below. A full 200-event queue clears that
  // ceiling easily, so the LAST actions before someone closed the tab — the ones a support
  // trail is actually read for — were the ones being thrown away. Send fewer, keep the rest
  // queued: if the page really is going away they are lost either way, but a backgrounded
  // tab that comes back will flush them.
  const take = unloading ? Math.min(MAX_QUEUE, 60) : MAX_QUEUE;
  const body = JSON.stringify({
    events: queue.splice(0, take),
    sessionId: sessionId(),
    viewAsUserId: viewAsUserId()
  });
  if (!unloading && dropped > 0) dropped = 0;

  try {
    fetch(ENDPOINT, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: "Bearer " + localStorage.token
      },
      body,
      // keepalive lets the request outlive the page during an unload flush.
      keepalive: !!unloading,
      credentials: "omit"
    }).catch(() => {});   // telemetry: never surface, never retry
  } catch (e) { /* ignore */ }
}

/* ---------------------------------------------------------------- page views */
export function recordPage(to) {
  try {
    if (!to) return;
    push({
      kind: "page",
      routeName: clip(to.name, 64),
      // fullPath carries the query; the SERVER rebuilds it from an allow-list and
      // refuses credential routes outright. Sent as-is only because the server is
      // the authority — never trust this string downstream.
      routePath: clip(to.fullPath || to.path, 400),
      companyCode: companyCode()
    });
  } catch (e) { /* never break navigation */ }
}

/* ------------------------------------------------------------------ searches */
export function recordSearch(source, term, resultCount) {
  try {
    push({
      kind: "search",
      searchSource: clip(source, 32),
      searchTerm: clip(term, 200),
      resultCount: (typeof resultCount === "number" && isFinite(resultCount)) ? resultCount : null,
      routeName: clip(window.__jcsRouteName, 64),
      companyCode: companyCode()
    });
  } catch (e) { /* never break a search */ }
}

/* ------------------------------------------------------------------- actions */
export function recordAction(name, target) {
  try {
    push({
      kind: "action",
      actionName: clip(name, 64),
      actionTarget: clip(target, 200),
      routeName: clip(window.__jcsRouteName, 64),
      companyCode: companyCode()
    });
  } catch (e) { /* never break the action */ }
}

/* -------------------------------------------------------------------- clicks */

/* Inputs whose mere presence near a click must not tempt anyone into capturing
   content. We record the CONTROL, never its value — but password and hidden
   fields are skipped entirely, belt and braces. */
function isSensitiveField(el) {
  if (!el || el.tagName !== "INPUT") return false;
  const t = (el.getAttribute("type") || "").toLowerCase();
  return t === "password" || t === "hidden";
}

/* The element's OWN label, not its whole subtree.
   el.textContent concatenates every descendant with no separator, which turned an
   Account Setup category header into:
     "+Event Logs & HistoryEvery change & system event across all"
   — the caret, the title, the join hint and the count run together. Direct text
   nodes first; if there are none (the common <button><span>Save</span></button>
   shape) take only the FIRST element child, so sibling hints, counts and badges
   stay out of the label. */
function ownText(el) {
  let t = "";
  const kids = el.childNodes || [];
  for (let i = 0; i < kids.length; i++) {
    if (kids[i].nodeType === 3) t += kids[i].nodeValue;   // TEXT_NODE
  }
  t = t.replace(/\s+/g, " ").trim();
  if (t) return t;
  // No direct text of its own. Gather each descendant's OWN text separately — never
  // el.textContent, which flattens the subtree and produced the exact run-together output
  // this function exists to prevent (live rows captured
  // "+Event Logs & HistoryEvery change & system event ac" before this).
  //
  // Then take the first piece that reads like a LABEL, skipping the noise. Taking the
  // literal first gave "Clicked span — +" for an accordion header, because the caret is the
  // first child; taking the longest gave the grey hint underneath, because a description is
  // longer than a title. Document order with the junk filtered out lands on the title —
  // "Event Logs & History" — which is what a person would call that control.
  const pieces = [];
  (function walk(node, depth) {
    if (!node || depth > 3) return;
    const kids = node.children || [];
    for (let i = 0; i < kids.length && pieces.length < 12; i++) {
      let own = "";
      const cn = kids[i].childNodes || [];
      for (let j = 0; j < cn.length; j++) if (cn[j].nodeType === 3) own += cn[j].nodeValue;
      own = own.replace(/\s+/g, " ").trim();
      if (own) pieces.push(own);
      walk(kids[i], depth + 1);
    }
  })(el, 0);
  if (!pieces.length) return "";
  for (let i = 0; i < pieces.length; i++) if (labelish(pieces[i])) return pieces[i];
  return pieces[0];
}

/* Does this read like a control LABEL, or like page content?
   The redaction contract for clicks is "record the CONTROL, never its contents", and the
   only thing separating the two in free text is length: real labels are short ("Save",
   "Browse User Insights"), whereas a customer email body, a notation or a spec block runs
   on. Without this guard a click on rendered email text wrote the first 60 characters of
   that customer's message into the trail, kept 12 months. */
const LABEL_MAX = 40;
function labelish(t) {
  return !!t && t.length > 1 && t.length <= LABEL_MAX;
}

/* A short, human-readable descriptor of what was clicked. Explicitly never
   el.value — that is the difference between an audit trail and a credential
   store (Login.vue, Registration.vue, Information.vue and CreateNewPassword.vue
   are all plain v-model password inputs). */
/* Icon-only controls (⟳, ×, +, 🐞) have no text, no title and no aria-label, so every
   normal fallback comes back empty and the row reads "Clicked button" — useless. These
   pull a name out of what IS there. */
function iconHint(el) {
  // Icon fonts carry the meaning in the class: "fa fa-refresh" -> "refresh".
  const kid = el.querySelector ? el.querySelector("i,svg,use") : null;
  const cls = ((kid && kid.getAttribute("class")) || el.className || "") + "";
  const m = cls.match(/(?:fa|icon|glyphicon)[-\s]([a-z0-9-]{3,})/i);
  if (m) return m[1].replace(/-/g, " ");
  return null;
}

/* The label a person would use for a field: its placeholder, its <label>, or its name. */
function fieldIdentity(el) {
  const byFor = el.id && document.querySelector ? document.querySelector('label[for="' + el.id + '"]') : null;
  return (
    el.getAttribute("placeholder") ||
    el.getAttribute("aria-label") ||
    (byFor ? (byFor.textContent || "").replace(/\s+/g, " ").trim() : "") ||
    el.getAttribute("name") ||
    null
  );
}

/* A real control, as opposed to whatever <div> happened to be under the pointer.
   `meaningful()` walks up only 4 levels, so a click on ordinary page text falls through
   and returns that text's own element — which must NOT then have its prose recorded. */
function isControl(el, tag) {
  return (
    tag === "button" || tag === "a" || tag === "select" ||
    tag === "input" || tag === "textarea" || tag === "label" || tag === "option" ||
    el.getAttribute("role") === "button" ||
    el.hasAttribute("data-activity")
  );
}

function describe(el) {
  if (!el) return null;
  const tag = (el.tagName || "").toLowerCase();
  const id = el.id ? "#" + el.id : "";
  const control = isControl(el, tag);
  const label =
    el.getAttribute("data-activity") ||
    el.getAttribute("aria-label") ||
    el.getAttribute("title") ||
    // Free text is read ONLY off a control. On a plain element it is page content, not a
    // label, and recording it would re-admit exactly what the redaction rule excludes.
    (control ? ownText(el) : null) ||
    // A <button value="x"> or <input type="submit" value="Save"> — value IS the label here,
    // and only here. Never read for text/password inputs; see valueIfSafe.
    ((tag === "button" || (tag === "input" && /^(submit|button|reset)$/i.test(el.getAttribute("type") || "")))
      ? el.getAttribute("value") : null) ||
    (tag === "input" || tag === "textarea" || tag === "select" ? fieldIdentity(el) : null) ||
    iconHint(el) ||
    // Last resort: name the CONTAINER, so "Clicked button" at least becomes
    // "button (in Event Logs & History)" rather than nothing at all. Only short,
    // label-shaped text qualifies — a long run is the container's CONTENT, and the
    // uiContext field already answers "where was this" properly.
    (function () {
      let n = el.parentElement;
      for (let i = 0; n && i < 3; i++) {
        const t = ownText(n);
        if (labelish(t)) return "in " + t;
        n = n.parentElement;
      }
      return null;
    })();
  const short = clip(label, 60);
  const typed = valueIfSafe(el);
  return clip(tag + id + (short ? " — " + short : "") + (typed ? ' = "' + typed + '"' : ""), 200);
}

/* ---------------------------------------------------------------------------
   WHAT WAS TYPED — allowed, with three hard exclusions that are not negotiable,
   because the alternative is writing live credentials into an audit table:

     1. password and hidden inputs — never, under any circumstance;
     2. ANY field on /login, /resetpassword, /createnewpassword, /registration;
     3. ANY field inside a form that contains a password input — this catches the
        "email" box sitting next to the password box, and any future sign-in form
        nobody remembered to add to the route list above.

   Everything else — searches, filters, quantities, notes — is captured, capped at
   80 characters. Committed search terms are ALSO recorded separately with their
   funnel and result count; this covers the boxes that are not searches.
   --------------------------------------------------------------------------- */
function valueIfSafe(el) {
  try {
    const tag = (el.tagName || "").toLowerCase();
    if (tag !== "input" && tag !== "textarea") return null;

    const type = (el.getAttribute("type") || "text").toLowerCase();
    if (type === "password" || type === "hidden") return null;

    const path = (window.location && window.location.pathname ? window.location.pathname : "").toLowerCase();
    if (/\/(login|resetpassword|createnewpassword|registration)/.test(path)) return null;

    const form = el.form || (el.closest ? el.closest("form") : null);
    if (form && form.querySelector && form.querySelector('input[type="password"]')) return null;

    return clip(el.value, 80);
  } catch (e) { return null; }
}

/* WHERE IN THE PAGE, not just which page.
   The route is often too coarse to be useful: Account Setup is an overlay on the
   reports dashboard, so clicking Generate Insights inside
   Account Setup → Event Logs & History → Browse User Insights records the route
   "Reports Dashboard" — technically true and practically useless.

   Any element can declare its context with data-activity-context, and the nearest
   one wins, so a panel can say exactly where it is (including its current mode).
   Absent that, fall back to the nearest dialog/section heading, which improves
   every existing screen without touching a single component. */
function uiContext(el) {
  try {
    let n = el;
    for (let i = 0; n && i < 12; i++) {
      if (n.getAttribute) {
        const explicit = n.getAttribute("data-activity-context");
        if (explicit) return clip(explicit, 200);
      }
      n = n.parentElement;
    }
    // Fallback: the closest thing on screen that names this region.
    const host = el.closest ? el.closest('[role="dialog"],section,.account-setup-item') : null;
    const head = host && host.querySelector
      ? host.querySelector('h1,h2,h3,.account-setup-row-label,.asc-card-title')
      : null;
    return head ? clip((head.textContent || "").replace(/\s+/g, " ").trim(), 200) : null;
  } catch (e) { return null; }
}

/* Walk up to the nearest thing a person would say they clicked, so we log
   "button — Add to Cart" rather than the <span> inside it. */
function meaningful(el) {
  let n = el;
  for (let i = 0; n && i < 4; i++) {
    const tag = (n.tagName || "").toLowerCase();
    if (tag === "button" || tag === "a" || tag === "select" ||
        tag === "label" || tag === "input" || tag === "textarea" ||
        n.getAttribute?.("role") === "button" || n.hasAttribute?.("data-activity")) {
      return n;
    }
    n = n.parentElement;
  }
  return el;
}

export function installClickRecorder() {
  try {
    // Capture phase, so a handler calling stopPropagation cannot hide the click.
    // passive: never interferes with the app's own handling.
    document.addEventListener("click", (e) => {
      try {
        const el = meaningful(e.target);
        if (!el || isSensitiveField(el)) return;
        push({
          kind: "click",
          actionName: (el.tagName || "").toLowerCase(),
          actionTarget: describe(el),
          uiContext: uiContext(el),
          routeName: clip(window.__jcsRouteName, 64),
          companyCode: companyCode()
        });
      } catch (err) { /* never break a click */ }
    }, { capture: true, passive: true });

    // WHAT WAS TYPED. A click on a field fires BEFORE they type, so the click row can
    // only ever carry the value the field already had — usually empty. "change" fires
    // when editing finishes (blur or Enter), which is the moment the typed value exists.
    // Recorded as an action, not a click, so the two do not blur together in the trail.
    // "input" was rejected: it fires per keystroke and would record "b","be","bel".
    document.addEventListener("change", (e) => {
      try {
        const el = e.target;
        if (!el) return;
        const tag = (el.tagName || "").toLowerCase();
        if (tag !== "input" && tag !== "textarea" && tag !== "select") return;
        if (isSensitiveField(el)) return;

        let typed = null;
        if (tag === "select") {
          // A select's value is an id; the visible option text is what a person read.
          const opt = el.options && el.selectedIndex >= 0 ? el.options[el.selectedIndex] : null;
          typed = clip(opt ? opt.text : el.value, 80);
        } else {
          typed = valueIfSafe(el);   // enforces every credential exclusion
        }
        if (!typed) return;

        push({
          kind: "action",
          actionName: "field-entry",
          actionTarget: clip((fieldIdentity(el) || tag) + ' = "' + typed + '"', 200),
          uiContext: uiContext(el),
          routeName: clip(window.__jcsRouteName, 64),
          companyCode: companyCode()
        });
      } catch (err) { /* never break an edit */ }
    }, { capture: true, passive: true });

    // Send what is queued before the tab goes away. visibilitychange fires
    // reliably on mobile backgrounding where unload does not.
    document.addEventListener("visibilitychange", () => {
      if (document.visibilityState === "hidden") flush(true);
    });
    window.addEventListener("pagehide", () => flush(true));
  } catch (e) { /* ignore */ }
}

export default {
  recordPage,
  recordSearch,
  recordAction,
  installClickRecorder
};
